// @ts-strict-ignore
import {
  Box, Button, ButtonVariant, Modal, Spinner, TextArea, useSnackBar,
} from '@usealloy/component-library';
import get from 'lodash/get';
import noop from 'lodash/noop';
// @ts-expect-error -- Lodash wont allow synthetic defaults and named imports without changing our TSConfig, which could have further reaching implications
import partial, { placeholder } from 'lodash/partial';
import partialRight from 'lodash/partialRight';
import type { ReactElement } from 'react';
import React, {
  useContext, useEffect, useRef, useState,
} from 'react';
import styled from 'styled-components';

import type { EntityNote } from '~/application-review/redux/types';
import ExportCSVIcon from '~/dashboard/components/ExportCSVIcon';
import { getApplicationTypeFromToken } from '~/helpers/applications';
import { MessageContext, UserContext } from '~/dashboard/context';
import NotesList from '~/notes/NotesList';
import { usePermissions } from '~/helpers/hooks';
import apiCalls from '~/helpers/apiCalls';

import * as helpers from './helpers';
import { datadogRum } from '@datadog/browser-rum';

type AddingNote = {
  entity_token?: string;
  key?: 'entity_id' | 'evaluation_id' | 'document_id' | 'review_id' | 'journey_application_id';
  row_id?: number | null;
  title?: string;
  token: string;
  table?: string;
};

type Note = {
  text?: string;
  agent_name?: string;
  formatted_timestamp?: string;
};

export type Props = {
  addingNoteTo: boolean | AddingNote;
  customAjax?: Function;
  generateCSV?: Function;
  getState?: boolean | Function;
  isNotesModalOpen?: boolean;
  noteCanBeAdded?: boolean;
  notes?: Note[];
  onAddNote?: (note: EntityNote) => void;
  onError?: Function;
  toggleModal?: boolean | Function;
  trigger?: boolean | ReactElement;
  updateState?: Function;
};

type State = {
  formErrors: string[];
  isButtonDisabled: boolean;
  isNotesModalOpen: boolean;
  isSubmitting: boolean;
  note: string;
};

const NotesModalDefaultProps = {
  generateCSV: noop,
  getState: false,
  noteCanBeAdded: true,
  notes: [],
  toggleModal: false,
  trigger: false,
};

const StyledCSVWrapper = styled.div`
  cursor: pointer;
  position: relative;
  right: 14px;
`;

function NotesModalCta(
  { isButtonDisabled, isSubmitting }: Pick<State, 'isButtonDisabled' | 'isSubmitting'>,
) {
  return (
    <Button
      disabled={isButtonDisabled}
      form="notes-form"
      id="notes-submit"
      type="submit"
      variant={ButtonVariant.primary}
    >
      {isSubmitting && <Spinner color="white" />}
      {' Add Note'}
    </Button>
  );
}

export default function NotesModal({
  addingNoteTo,
  isNotesModalOpen,
  notes,
  trigger,
  updateState,
  ...props
}: Props) {
  const addSnackBar = useSnackBar();
  const { createMessage: createDashboardMessage } = useContext(MessageContext);
  const { featureFlags, typhonDomain } = useContext(UserContext);
  const { notes: { createNote } } = apiCalls;
  const isHideAllCsvExportButtonsFlagEnabled = Boolean(featureFlags?.hideAllCsvExportButtons)
      || false;
  const createMessage = (message, type) => (
    createDashboardMessage
      ? createDashboardMessage(message)
      : addSnackBar({
        type,
        body: message,
        title: '',
      })
  );


  const [permissions] = usePermissions();
  const [state, setState] = useState<State>({
    formErrors: [],
    isButtonDisabled: true,
    isNotesModalOpen: false,
    isSubmitting: false,
    note: '',
  });

  const { isSubmitting } = state;
  const applicationType = (
    addingNoteTo ? getApplicationTypeFromToken(get(addingNoteTo, 'token'), featureFlags?.alertGenerationInJourneys) : ''
  );

  const handleToggleModal = partial(
    helpers.handleToggleModal,
    placeholder,
    // If we pass in a setState from a parent then we want to control the
    // isNotesModalOpen with that state management rather than the internal one in
    // in this component in order to maintain compatibility with the various
    // places this modal is used in the app
    (updateState ?? setState),
    state,
    props.toggleModal,
  );
  const handleNoteFormSubmit = partial(
    helpers.handleNoteFormSubmit,
    placeholder,
    props.customAjax ?? (note => createNote({
      typhonDomain,
      body: {
        text: note,
        ...(typeof addingNoteTo === 'object' ? addingNoteTo : {}),
      },
    })),
    applicationType,
    props.onAddNote,
    // @ts-expect-error -- Lodash expects 1-5 agruments max
    setState,
    state,
    handleToggleModal,
    { createMessage, getState: props.getState, onError: props.onError },
  ) as React.FormEventHandler<HTMLFormElement>;

  const handleNoteInput = partialRight(helpers.handleNoteInput, setState);
  const onClose = () => {
    if (typeof props.toggleModal !== 'boolean') {
      props.toggleModal();
    }

    (partial(helpers.onClose, placeholder, updateState ?? setState) as () => void)();
  };
  const triggerRef = useRef(null);

  const hasAddingNoteTo = !!addingNoteTo;

  useEffect(() => {
    setState(currentState => ({
      ...currentState,
      isNotesModalOpen: helpers.determineIfNotesModalShouldBeOpen(
        hasAddingNoteTo,
        isNotesModalOpen,
        trigger,
      ),
    }));
  }, [hasAddingNoteTo, isNotesModalOpen, trigger]);

  return (
    <>
      {(
        trigger && !(typeof trigger === 'boolean')
          ? React.cloneElement(
            trigger, { onClick: handleToggleModal, ref: triggerRef },
          )
          : null
      )}
      <Modal
        header={(
          <Box
            alignItems="flex-start"
            direction="row"
            justifyContent="flex-start"
          >
            <h2>
              {(
                notes.length
                  ? `${applicationType} Notes`
                  : `Add ${applicationType} Note`
              )}
            </h2>
            {props.generateCSV !== noop
              && Boolean(notes?.length)
              && !isHideAllCsvExportButtonsFlagEnabled && (
              <StyledCSVWrapper
                className="ml-5"
                onClick={() => {
                  datadogRum.addAction('csv_export', { resource: 'notes' });
                  props.generateCSV();
                }}
                data-dd-action-name="alloydd-export-csv-btn"
                role="button"
                aria-label="Export CSV"
              >
                <ExportCSVIcon />
              </StyledCSVWrapper>
            )}
          </Box>
        )}
        id="notes-modal"
        isOpen={state.isNotesModalOpen || isNotesModalOpen}
        onClose={onClose}
        primaryAction={(
          <NotesModalCta
            isButtonDisabled={state.isButtonDisabled}
            isSubmitting={isSubmitting}
          />
        )}
        secondaryAction={(
          <Button
            disabled={isSubmitting}
            onClick={onClose}
            variant={ButtonVariant.tertiary}
          >
            Close
          </Button>
        )}
      >
        <Box
          alignItems="flex-start"
          direction="column"
          justifyContent="flex-start"
        >
          {notes.length ? (
            <div className="row header-24 w-100">
              <div className="col small-text">
                <NotesList notes={notes} />
              </div>
            </div>
          ) : null}
          {props.noteCanBeAdded && get(permissions, "['CREATE:Notes:null']") ? (
            <form
              id="notes-form"
              onSubmit={handleNoteFormSubmit}
              style={{ width: '100%' }}
            >
              {state.formErrors.map(error => (
                <div key={error} className="alert alert-danger small-text">
                  <i className="fas fa-fw fa-lg fa-exclamation-triangle" />
                  {` ${error}`}
                </div>
              ))}
              <TextArea
                id="new-note-text"
                label={`Add a note to this ${applicationType}:`}
                name="document_notes"
                onChange={handleNoteInput}
                value={state.note}
              />
            </form>
          ) : null}
        </Box>
      </Modal>
    </>
  );
}

NotesModal.defaultProps = NotesModalDefaultProps;
